import { useRef, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import { CategoryParser, ExcelParser } from '@utils/index'
import type { CategoryParseResult, ParseResult } from '@utils/index'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { money } from '@core/utils/format'
import { badgeMark, badgeWeight, credInput, errBanner, okBanner } from '@ui/admin'
import { Marking, Scales } from '@ui/icons'

type ImportPreview =
  | {
      kind: 'products'
      result: ParseResult
      fileName: string
      warnings: string[]
    }
  | {
      kind: 'categories'
      result: CategoryParseResult
      fileName: string
    }

/**
 * Локальный импорт каталога: товары или справочник категорий.
 *
 * Товары создают новую версию каталога НА ЭТОЙ кассе. Категории — tenant-level
 * справочник, который не входит в версию и не откатывается вместе с ней.
 */
export function PriceImportSection({
  core,
  onImported
}: {
  core: AppCore
  onImported?: () => void
}) {
  const { catalog } = core
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  /** Выбор файла → парсинг → предпросмотр (без записи в БД). */
  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    setMessage(null)
    const file = e.target.files?.[0]
    if (!file) {
      return
    }
    const buffer = await file.arrayBuffer()

    // Сначала пробуем выгрузку категорий: у неё нет цены, поэтому товарный
    // парсер её не примет. Если не подошла — парсим как товары.
    try {
      const result = new CategoryParser().parse(buffer)
      setPreview({ kind: 'categories', result, fileName: file.name })
      return
    } catch {
      // не выгрузка категорий — идём дальше
    }

    try {
      const result = new ExcelParser().parse(buffer)
      const resolved = await catalog.resolveCategoryParents(result.rows)
      setPreview({
        kind: 'products',
        result,
        fileName: file.name,
        warnings: resolved.warnings
      })
    } catch (err: unknown) {
      setPreview(null)
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /** Подтвердить импорт предпросмотра. */
  const confirmImport = async () => {
    if (!preview) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (preview.kind === 'categories') {
        const report = await catalog.importCategories(preview.result.rows)
        setMessage(
          `Категории: добавлено ${report.added}, обновлено ${report.updated}. ` +
            `Пропущено строк: ${preview.result.skipped}.` +
            (preview.result.warnings.length
              ? ` Предупреждения: ${preview.result.warnings.join('; ')}`
              : '')
        )
      } else {
        const stamp = new Date().toLocaleString('ru-RU')
        const versionName = `${preview.result.presetId.toUpperCase()} • ${stamp}`
        const version = await catalog.importAsNewVersion(
          preview.result.rows,
          versionName,
          `Импорт из «${preview.fileName}» (пресет ${preview.result.presetId})`
        )
        setMessage(
          `Импортировано ${preview.result.rows.length} товаров в версию «${version.versionName}». ` +
            `Пропущено строк: ${preview.result.skipped}. Маркировка молочки: ${preview.result.markedCount}.` +
            (preview.warnings.length
              ? ` Предупреждения: ${preview.warnings.join('; ')}`
              : '')
        )
      }
      setPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      onImported?.()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const cancelPreview = () => {
    setPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div>
      <p className={hint}>
        Поддерживаются выгрузки товаров (Контур / СБИС / FusionPOS / 1С) и выгрузка
        категорий (название, родитель, отдел, активно). Формат определяется автоматически.
      </p>

      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={onFileSelected}
        disabled={busy}
        className={credInput}
      />

      {preview?.kind === 'products' && (
        <div className="mt-3">
          <p className="my-2">
            Файл: <strong>{preview.fileName}</strong> · Пресет:{' '}
            <strong>{preview.result.presetId}</strong> · Строк:{' '}
            <strong>{preview.result.rows.length}</strong> · Пропущено:{' '}
            <strong>{preview.result.skipped}</strong> · Маркировка молочки:{' '}
            <strong>{preview.result.markedCount}</strong>
          </p>
          <p className="my-2">
            Категорий:{' '}
            <strong>
              {new Set(preview.result.rows.map((r) => r.category?.trim() || 'Общее')).size}
            </strong>{' '}
            · Весовых позиций:{' '}
            <strong>{preview.result.rows.filter((r) => r.unit === 'кг').length}</strong>
          </p>
          {preview.warnings.length > 0 && (
            <p className={errBanner}>{preview.warnings.join('; ')}</p>
          )}
          <div className={tableWrap}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Наименование</th>
                  <th className={th}>Категория</th>
                  <th className={th}>Штрихкод</th>
                  <th className={thNum}>Цена</th>
                  <th className={th}>Маркировка</th>
                </tr>
              </thead>
              <tbody>
                {preview.result.rows.slice(0, 50).map((r, i) => (
                  <tr key={i}>
                    <td className={td}>
                      {r.name}
                      {r.unit === 'кг' && (
                        <span className={cx(badgeWeight, 'ml-1.5')}>
                          <Scales size={16} /> кг
                        </span>
                      )}
                    </td>
                    <td className={td}>
                      {r.categoryParent && r.categoryParent !== r.category
                        ? `${r.categoryParent} / ${r.category || 'Общее'}`
                        : r.category || 'Общее'}
                    </td>
                    <td className={td}>{r.barcode ?? '—'}</td>
                    <td className={tdNum}>{money(r.price)}</td>
                    <td className={td}>
                      {r.requiresMarking ? (
                        <span className={badgeMark} title={r.markingKeyword}>
                          <span className="inline-flex items-center gap-1">
                            <Marking size={16} /> молоко
                          </span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.result.rows.length > 50 && (
              <p className={hint}>
                Показаны первые 50 из {preview.result.rows.length} строк.
              </p>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={confirmImport}
              disabled={busy}
              className={cx(btnBase, btn.secondary, btnSize.sm)}
            >
              Импортировать как новую версию
            </button>
            <button
              onClick={cancelPreview}
              disabled={busy}
              className={cx(btnBase, btn.secondary, btnSize.sm)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {preview?.kind === 'categories' && (
        <div className="mt-3">
          <p className="my-2">
            Файл: <strong>{preview.fileName}</strong> · Пресет:{' '}
            <strong>{preview.result.presetId}</strong> · Категорий:{' '}
            <strong>{preview.result.rows.length}</strong> · Пропущено:{' '}
            <strong>{preview.result.skipped}</strong>
          </p>
          {preview.result.warnings.length > 0 && (
            <p className={errBanner}>{preview.result.warnings.join('; ')}</p>
          )}
          <div className={tableWrap}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Название</th>
                  <th className={th}>Родитель</th>
                  <th className={th}>Отдел</th>
                  <th className={th}>Активно</th>
                </tr>
              </thead>
              <tbody>
                {preview.result.rows.slice(0, 50).map((r, i) => (
                  <tr key={i}>
                    <td className={td}>{r.name}</td>
                    <td className={td}>{r.parentName ?? 'Верхний уровень'}</td>
                    <td className={td}>{r.department ?? '—'}</td>
                    <td className={td}>{r.isActive ? 'да' : 'нет'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.result.rows.length > 50 && (
              <p className={hint}>
                Показаны первые 50 из {preview.result.rows.length} строк.
              </p>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={confirmImport}
              disabled={busy}
              className={cx(btnBase, btn.secondary, btnSize.sm)}
            >
              Импортировать категории
            </button>
            <button
              onClick={cancelPreview}
              disabled={busy}
              className={cx(btnBase, btn.secondary, btnSize.sm)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
